import { useState, useEffect, useCallback, memo } from "react";
import Layout from "@/components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, X, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { projects } from "@/data/projectsData";

const services = [
  "Branding",
  "Marketing",
  "Brand Strategy",
  "Marketing Strategy",
  "Visual Identity",
  "Video Production",
  "Personal Branding",
  "Performance Marketing",
  "Brand Marketing",
  "Podcast",
  "Graphic Design",
  "Content Writing",
];

const sectors = [
  { name: "Everyone", desc: "For brands of all sizes seeking clarity." },
  { name: "Startups", desc: "Agile branding for the next generation of industry leaders." },
  { name: "EdTech & Education Services", desc: "Scaling trust and clarity in global learning markets." },
  { name: "Health, Wellness & Fitness", desc: "Building admired brands in the health and vitality space." },
  { name: "Food, Beverage & Hospitality", desc: "Creating sensory-led identities for the plate and the stay." },
  { name: "Creative & Media Businesses", desc: "Positioning the players behind the culture." },
  { name: "Technology & SaaS Startups", desc: "Defining clear signals in complex digital ecosystems." },
  { name: "E-commerce & D2C Brands", desc: "Driving direct-to-consumer growth with strategic clarity." },
  { name: "Professional Services & Consultants", desc: "Converting expertise into authoritative market presence." },
  { name: "Real Estate & Construction", desc: "Branding the spaces where people live and build." },
  { name: "Retail & Local Businesses", desc: "Strengthening community trust and physical availability." },
  { name: "Social Impact & Purpose-Driven Ventures", desc: "Amplifying the signals of mission-led growth." },
];

// Map projects from projectsData to carousel format
const showcaseProjectIds = [
  "forli",
  "forlingo-academy",
  "ariv",
  "zameel-publications",
  "pune-ponnonam"
];

const projectItems = projects
  .filter(project => showcaseProjectIds.includes(project.id))
  .sort((a, b) => showcaseProjectIds.indexOf(a.id) - showcaseProjectIds.indexOf(b.id))
  .map(project => ({
    id: project.id,
    title: project.title,
    category: project.industry,
    image: project.heroImage,
    tags: project.services,
  }));

/**
 * RotatingDrum Component
 * Creates a "slot machine" scrolling effect when the target index changes.
 */
const RotatingDrum = memo(({
  items,
  currentIndex,
  activePanel,
  direction = "up"
}: {
  items: string[],
  currentIndex: number,
  activePanel: boolean,
  direction?: "up" | "down"
}) => {
  return (
    <div className="relative h-[1.2em] overflow-hidden flex items-center pointer-events-none px-1">
      <AnimatePresence mode="wait">
        <motion.span
          key={currentIndex}
          initial={{ y: direction === "up" ? "100%" : "-100%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: direction === "up" ? "-100%" : "100%", opacity: 0 }}
          transition={{
            duration: 0.35,
            ease: [0.19, 1, 0.22, 1]
          }}
          className={`h-[1.2em] flex items-center whitespace-nowrap ${activePanel ? "text-black" : "text-purple group-hover:text-black"
            } transition-colors`}
        >
          {items[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
});

const ServicesTable = ({ sections }: { sections: any[] }) => {
  const [activeId, setActiveId] = useState<string | null>("branding");

  return (
    <div className="flex flex-col lg:flex-row w-full gap-8 lg:items-start">
      {/* 5 Service Titles - Vertical Table Format */}
      <div className="w-full lg:w-[400px] shrink-0 border-t border-black/10">
        {sections.map((section, idx) => (
          <button
            key={section.id}
            onClick={() => setActiveId(activeId === section.id ? null : section.id)}
            className={`w-full flex items-center justify-between py-8 px-4 border-b border-black/10 transition-all duration-300 group hover:bg-black/5 ${activeId === section.id ? "bg-purple/10 border-b-purple" : ""
              }`}
          >
            <div className="flex items-center gap-6">
              <span className={`text-sm font-bold font-mono transition-colors duration-300 ${activeId === section.id ? "text-purple" : "text-black/30 group-hover:text-black/60"
                }`}>
                0{idx + 1}
              </span>
              <h3 className={`text-xl md:text-2xl font-bold tracking-tighter transition-all duration-300 ${activeId === section.id ? "text-purple translate-x-2" : "text-black"
                }`}>
                {section.title}
              </h3>
            </div>

            <ChevronRight
              size={24}
              className={`transition-all duration-500 ${activeId === section.id ? "rotate-90 lg:rotate-180 text-purple" : "text-black/20 group-hover:text-black/60 group-hover:translate-x-1"
                }`}
            />
          </button>
        ))}
      </div>

      {/* Emergent Content Panel */}
      <div className="flex-1 relative min-h-[400px]">
        <AnimatePresence mode="wait">
          {activeId ? (
            <motion.div
              key={activeId}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
              className="bg-black/[0.03] border border-black/5 rounded-2xl overflow-hidden p-6 md:p-12"
            >
              <div className="max-w-4xl">
                <div className="flex items-center gap-4 mb-10">
                  <div className="h-px bg-purple flex-1" />
                  <span className="text-purple font-mono font-bold tracking-widest text-sm">
                    {sections.find(s => s.id === activeId)?.title} Services
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  {sections.find(s => s.id === activeId)?.items.map((item: any, i: number) => (
                    <div key={i} className="group/item">
                      <h4 className="text-xl font-bold mb-3 text-black flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full bg-purple shrink-0 group-hover/item:scale-125 transition-transform" />
                        {item.title}
                      </h4>
                      {item.desc && (
                        <p className="text-black/60 font-light text-sm leading-relaxed pl-5 border-l border-black/5 group-hover/item:border-[#c0ff09]/30 group-hover/item:text-black transition-all duration-300">
                          {item.desc}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-full flex flex-col items-center justify-center text-center p-12 lg:border lg:border-dashed lg:border-black/20 lg:rounded-2xl"
            >
              <div className="w-16 h-16 rounded-full border border-black/10 flex items-center justify-center mb-6 text-black/20">
                <ChevronRight size={32} />
              </div>
              <p className="text-black/30 font-light tracking-widest text-sm">
                Select a service to explore details
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const serviceSections = [
  {
    id: "branding",
    title: "Branding",
    items: [
      {
        title: "Branding",
        desc: "Strategy, Segmentation, Targeting, Positioning (STP), Belief System, Brand Personality"
      },
      {
        title: "Visual Identity Design",
        desc: "Logo Systems (Primary, Secondary, Responsive), Color, Typography & Iconography Systems, Distinctive Brand Assets"
      },
      {
        title: "Personal Branding",
        desc: "Founder / Leadership Positioning, Thought-Leadership Strategy, Personal Brand Narrative & Voice, Reputation & Authority Building"
      }
    ]
  },
  {
    id: "marketing",
    title: "Marketing",
    items: [
      {
        title: "Diagnosis, Strategy, Tactics (DST)",
        desc: "We first identify the real problem, then define clear strategic choices, and finally execute focused actions that drive measurable results."
      },
      {
        title: "Brand Marketing",
        desc: "Builds long-term brand meaning, memory, and trust, so you’re chosen, not just noticed."
      },
      {
        title: "Performance Marketing",
        desc: "Drives measurable actions like leads and sales through data-led campaigns and optimization."
      }
    ]
  },
  {
    id: "video",
    title: "Video",
    items: [
      { title: "Video Podcasts" },
      { title: "Product & Explainer Videos" },
      { title: "Short-Form & Vertical Video Content" },
      { title: "Campaign & Ad Films" },
      { title: "Motion Graphics & Kinetic Typography" }
    ]
  },
  {
    id: "creative-writing",
    title: "Creative Writing",
    items: [
      {
        title: "Copywriting",
        desc: "Brand Voice & Tone Development, Website & Landing Page Copy, Campaign & Ad Copy, Taglines, Slogans & Naming"
      },
      {
        title: "Content Writing",
        desc: "Long-Form Articles & Blogs, Whitepapers, Reports & Case Studies, Scriptwriting for Video & Audio"
      }
    ]
  },
  {
    id: "design",
    title: "Design",
    items: [
      {
        title: "Print & Packaging Design",
        desc: "Product Packaging Systems, Labels & Regulatory-Ready Designs, Brochures, Catalogues & Collateral"
      },
      {
        title: "Editorial & Layout Design",
        desc: "Magazine & Journal Design, Annual Reports & Publications, Books & Coffee-Table Books, Digital Editorials & PDFs, Infographic & Data-Storytelling Design"
      },
      {
        title: "Social Media Collaterals",
        desc: "Social Media Posters & Creatives, Flyers, Banners & Digital Ads, Campaign & Promotional Creatives, Templates & Design Systems"
      },
      {
        title: "Website Design",
        desc: "Landing Pages & Microsites, Website Redesign & Optimization, Brand-Aligned UI Systems"
      }
    ]
  }
];

const WhatPage = () => {
  const [serviceIndex, setServiceIndex] = useState(0);
  const [sectorIndex, setSectorIndex] = useState(0);
  const [isServicePaused, setIsServicePaused] = useState(false);
  const [isSectorPaused, setIsSectorPaused] = useState(false);
  const [activePanel, setActivePanel] = useState<"services" | "sectors" | null>(null);
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);

  const nextProject = () => {
    setActiveProjectIndex((prev) => (prev + 1) % projectItems.length);
  };

  const prevProject = () => {
    setActiveProjectIndex((prev) => (prev - 1 + projectItems.length) % projectItems.length);
  };

  // Carousel auto-play logic (3s interval)
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveProjectIndex((prev) => (prev + 1) % projectItems.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Auto-rotation logic (Randomized and staggered)
  useEffect(() => {
    if (activePanel) return;

    const getRandomIndex = (currentIndex: number, length: number) => {
      if (length <= 1) return 0;
      let nextIndex = currentIndex;
      while (nextIndex === currentIndex) {
        nextIndex = Math.floor(Math.random() * length);
      }
      return nextIndex;
    };

    let rotationTimeout: NodeJS.Timeout;

    const rotateAll = () => {
      if (!isServicePaused && !isSectorPaused) {
        setServiceIndex((prev) => getRandomIndex(prev, services.length));
        setSectorIndex((prev) => getRandomIndex(prev, sectors.length));
      }

      // Random interval between 3s and 5s
      const delay = 3000 + Math.random() * 2000;
      rotationTimeout = setTimeout(rotateAll, delay);
    };

    rotationTimeout = setTimeout(rotateAll, 2000);

    return () => {
      clearTimeout(rotationTimeout);
    };
  }, [isServicePaused, isSectorPaused, activePanel]);

  const togglePanel = (panel: "services" | "sectors") => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") setActivePanel(null);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Layout>
      <section className="relative min-h-screen text-foreground overflow-x-hidden flex flex-col items-center py-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.img
              key={activeProjectIndex}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              src={projectItems[activeProjectIndex].image}
              alt={projectItems[activeProjectIndex].title}
              className="w-full h-full object-cover absolute inset-0"
              loading="eager"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="container-wide max-w-7xl mx-auto flex flex-col items-center relative z-10 p-6 my-auto">

          {/* Main Card Container */}
          <Reveal trigger="onLoad" delay={0.5}>
            <motion.div
              layout
              className={`relative bg-white/20 backdrop-blur-md shadow-sm border border-white/20 overflow-hidden transition-all duration-700 ease-[0.19,1,0.22,1] ${activePanel ? "w-full max-w-5xl rounded-lg p-6 md:p-12" : "rounded-lg px-4 py-2 md:px-8 md:py-4"
                }`}
            >
              <AnimatePresence>
                {activePanel && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col items-center"
                  >
                    {/* Close Button */}
                    <button
                      onClick={() => setActivePanel(null)}
                      className="absolute top-8 right-8 p-2 hover:bg-black/10 rounded-full transition-colors z-50 text-black/40 hover:text-black"
                    >
                      <X size={24} />
                    </button>

                    {/* Project Showcase Carousel */}
                    <div className="w-full relative group/carousel mb-10">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeProjectIndex}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                          className="w-full"
                        >
                          <Link to={`/projects/${projectItems[activeProjectIndex].id}`} className="block relative aspect-[16/9] rounded-xl overflow-hidden group/item">
                            <img
                              src={projectItems[activeProjectIndex].image}
                              alt={projectItems[activeProjectIndex].title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 md:p-12">
                              <p className="text-purple text-sm font-bold tracking-widest mb-2">
                                {projectItems[activeProjectIndex].category}
                              </p>
                              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                {projectItems[activeProjectIndex].title}
                              </h3>
                              <div className="flex items-center gap-2 text-white font-bold border-b-2 border-purple w-fit pb-1">
                                View Project <ArrowRight size={20} />
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      </AnimatePresence>

                      {/* Navigation Buttons */}
                      <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between pointer-events-none">
                        <button
                          onClick={prevProject}
                          className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300 pointer-events-auto opacity-0 group-hover/carousel:opacity-100"
                        >
                          <ArrowLeft size={24} />
                        </button>
                        <button
                          onClick={nextProject}
                          className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300 pointer-events-auto opacity-0 group-hover/carousel:opacity-100"
                        >
                          <ArrowRight size={24} />
                        </button>
                      </div>

                      {/* Dots */}
                      <div className="flex justify-center gap-2 mt-6">
                        {projectItems.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveProjectIndex(i)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${activeProjectIndex === i ? "bg-purple w-8" : "bg-black/20"
                              }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Tags Grid */}
                    <div className="flex flex-wrap justify-center gap-3 mb-16 max-w-4xl">
                      {(activePanel === "services" ? services : sectors.map(s => s.name)).map((tag) => (
                        <div
                          key={tag}
                          className="px-4 py-2 bg-white hover:bg-black hover:text-white text-black text-sm font-bold rounded-md transition-all duration-300 cursor-default border border-black/5"
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* The Floating Bar Trigger */}
              <motion.div
                layout
                className={`flex flex-col items-center ${activePanel ? "mt-auto" : ""}`}
              >
                <h1 className="text-base md:text-xl font-bold tracking-tighter leading-none flex items-center gap-x-[0.5em] whitespace-nowrap text-black">
                  <span className="shrink-0 text-black/40 font-bold">We do</span>

                  {/* Service Drum */}
                  <button
                    className="relative flex items-center gap-1 cursor-pointer text-left focus:outline-none group select-none"
                    onMouseEnter={() => setIsServicePaused(true)}
                    onMouseLeave={() => setIsServicePaused(false)}
                    onClick={() => togglePanel("services")}
                    onFocus={() => setIsServicePaused(true)}
                    onBlur={() => setIsServicePaused(false)}
                    aria-haspopup="true"
                    aria-expanded={activePanel === "services"}
                  >
                    <RotatingDrum
                      items={services}
                      currentIndex={serviceIndex}
                      activePanel={activePanel === "services"}
                      direction="up"
                    />
                    <ChevronDown size={16} className={`${activePanel === "services" ? "text-black rotate-180" : "text-purple"} transition-transform duration-300`} />
                  </button>

                  <span className="shrink-0 text-black/40 font-bold">for</span>

                  {/* Sector Drum */}
                  <button
                    className="relative flex items-center gap-1 cursor-pointer text-left focus:outline-none group select-none"
                    onMouseEnter={() => setIsSectorPaused(true)}
                    onMouseLeave={() => setIsSectorPaused(false)}
                    onClick={() => togglePanel("sectors")}
                    onFocus={() => setIsSectorPaused(true)}
                    onBlur={() => setIsSectorPaused(false)}
                    aria-haspopup="true"
                    aria-expanded={activePanel === "sectors"}
                  >
                    <RotatingDrum
                      items={sectors.map(s => s.name)}
                      currentIndex={sectorIndex}
                      activePanel={activePanel === "sectors"}
                      direction="down"
                    />
                    <ChevronDown size={16} className={`${activePanel === "sectors" ? "text-black rotate-180" : "text-purple"} transition-transform duration-300`} />
                  </button>
                </h1>
              </motion.div>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* Services Breakdown Section */}
      <section className="bg-white py-20 md:py-32 px-4 md:px-6 relative z-10 border-t border-black/5">
        <div className="container-wide max-w-7xl mx-auto">
          <ServicesTable sections={serviceSections} />
        </div>
      </section>

      {/* Principles Section */}
      <section className="violet-glow-section text-white py-32 px-6 lg:px-12">
        <div className="container-wide max-w-7xl mx-auto flex flex-col items-center text-center gap-12 relative z-10">
          <div className="max-w-4xl">
            <Reveal className="mx-auto mb-6 pb-2">
              <h2 className="text-4xl md:text-5xl font-bold leading-[1.1] tracking-tighter">
                Our pricing is <span className="relative inline-block whitespace-nowrap">
                  flexible and affordable
                  <svg className="absolute -bottom-3 left-0 w-full h-4 text-[#c0ff09]" viewBox="0 0 300 20" fill="none" preserveAspectRatio="none">
                    <path
                      d="M5 15C50 5 150 5 295 15"
                      stroke="currentColor"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="drop-shadow-sm"
                    />
                  </svg>
                </span>, shaped around your needs, contact us for a custom quote.
              </h2>
            </Reveal>
          </div>
          <Reveal className="mx-auto" delay={0.2}>
            <Button className="bg-white text-black hover:bg-black hover:text-white rounded-full px-8 py-6 text-lg font-bold transition-all duration-300 border-none" asChild>
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default WhatPage;
